import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  forwardRef,
  inject,
  Injector,
  Input,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import * as countries from 'i18n-iso-countries';
import { Country } from './country.model';
@Component({
  selector: 'app-select-country',
  imports: [FormsModule, CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select-country.html',
  styleUrl: './select-country.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectCountry),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SelectCountry),
      multi: true,
    },
  ],
})
export class SelectCountry implements ControlValueAccessor, Validator, OnInit, DoCheck {
  private injector = inject(Injector);

  @Input() label = 'Pays';
  @Input() value: string | undefined;
  @Input() required: boolean | string | null | undefined;
  @Input() showFlag = true;

  ngControl?: NgControl;
  disabled = false;
  cdr = inject(ChangeDetectorRef);

  @ViewChild(MatSelect) private readonly matSelect?: MatSelect;

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => {
      const ctrl = this.control;
      return !!(ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty));
    },
  };

  countryList: Country[] = [];

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null) ?? undefined;
    if (this.ngControl) {
      try {
        this.ngControl.valueAccessor = this;
      } catch {
        console.warn(
          'Impossible de définir le valueAccessor. Assurez-vous que ce composant est utilisé dans un contexte de formulaire (FormGroup, FormControl, etc.)',
        );
      }
    }
    const countryObj = countries.getNames('fr', { select: 'official' });

    this.countryList = Object.entries(countryObj)
      .map(([code, name]) => ({
        code,
        name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  ngDoCheck(): void {
    this.matSelect?.updateErrorState();
  }

  onChange: (value: string | undefined) => void = () => {
    // No-op
  };
  onTouched: () => void = () => {
    // No-op
  };
  writeValue(value: string | undefined): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const ctrl = control ?? this.control;
    const isRequired = this.isRequired || (ctrl ? this.hasRequiredValidator(ctrl) : false);

    if (isRequired && !this.value) {
      return { required: true };
    }

    return null;
  }

  get isRequired(): boolean {
    if (this.required !== undefined && this.required !== null && this.required !== '') {
      if (typeof this.required === 'boolean') {
        return this.required;
      }
      return this.required === 'true';
    }

    return this.control?.hasValidator(Validators.required) ?? false;
  }

  private hasRequiredValidator(control: AbstractControl): boolean {
    if (!control.validator) return false;

    const validator = control.validator({} as AbstractControl);
    return !!(validator && validator['required']);
  }

  /** Retourne le control du FormGroup / FormControl auquel ce composant est lié, si présent */
  get control(): AbstractControl | null {
    return this.ngControl ? (this.ngControl.control ?? null) : null;
  }

  onSelectionChange(value: string): void {
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
  }
}
